import { Controller } from "@hotwired/stimulus"
import { post, patch } from "@rails/request.js"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["itemContainer", "columnContainer"]

  initialize() {
    this.sortableMap = new Map()
    this.columnSortable = null
  }

  disconnect() {
    this.sortableMap.forEach(sortable => sortable.destroy())
    this.sortableMap.clear()
    if (this.columnSortable) {
      this.columnSortable.destroy()
      this.columnSortable = null
    }
  }

  columnContainerTargetConnected(element) {
    this.columnSortable = Sortable.create(element, {
      animation: 150,
      ghostClass: "opacity-50",
      chosenClass: "shadow-lg",
      dragClass: "cursor-grabbing",
      handle: ".column-drag-handle",
      draggable: ".roadmap-column",
      onEnd: this.handleColumnSortEnd.bind(this)
    })
  }

  columnContainerTargetDisconnected() {
    if (this.columnSortable) {
      this.columnSortable.destroy()
      this.columnSortable = null
    }
  }

  itemContainerTargetConnected(element) {
    const sortable = Sortable.create(element, {
      group: "roadmap-items",
      animation: 150,
      ghostClass: "opacity-50",
      chosenClass: "shadow-lg",
      dragClass: "cursor-grabbing",
      draggable: ".roadmap-item",
      onEnd: this.handleItemSortEnd.bind(this)
    })
    this.sortableMap.set(element, sortable)
  }

  itemContainerTargetDisconnected(element) {
    const sortable = this.sortableMap.get(element)
    if (sortable) {
      sortable.destroy()
      this.sortableMap.delete(element)
    }
  }

  // --- Add Item (inline textarea, matching canvas addCard pattern) ---

  addItem(event) {
    const columnId = event.currentTarget.dataset.columnId
    const container = event.currentTarget
      .closest(".roadmap-column")
      .querySelector('[data-roadmap-sortable-target="itemContainer"]')

    const tempCard = document.createElement("div")
    tempCard.className = "roadmap-item bg-neutral-primary border border-brand/50 rounded-lg p-3 shadow-sm"
    tempCard.innerHTML = `
      <textarea
        class="w-full text-sm p-0 border-0 resize-none focus:ring-0 focus:outline-none bg-transparent text-heading placeholder-body-subtle"
        placeholder="Enter item title..."
        rows="2"
        data-action="blur->roadmap-sortable#saveNewItem keydown.enter->roadmap-sortable#saveNewItemOnEnter keydown.esc->roadmap-sortable#cancelNewItem"
        data-column-id="${columnId}"
      ></textarea>
    `
    container.appendChild(tempCard)

    const textarea = tempCard.querySelector("textarea")
    textarea.focus()
  }

  async saveNewItem(event) {
    const textarea = event.currentTarget
    const tempCard = textarea.closest(".roadmap-item")

    if (tempCard.dataset.saving === "true") return

    const title = textarea.value.trim()
    const columnId = textarea.dataset.columnId

    if (!title) {
      this.cancelNewItem(event)
      return
    }

    tempCard.dataset.saving = "true"

    try {
      const response = await post("/workspace/roadmap_items", {
        body: JSON.stringify({
          roadmap_item: {
            roadmap_column_id: columnId,
            title: title
          }
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        // Replace temp card with the real item card
        tempCard.outerHTML = data.html
        // Update the column item count
        this.updateColumnCount(columnId)
      } else {
        const data = await response.json
        console.error("Failed to create item:", data.error)
        tempCard.dataset.saving = "false"
      }
    } catch (error) {
      console.error("Error creating item:", error)
      tempCard.dataset.saving = "false"
    }
  }

  saveNewItemOnEnter(event) {
    if (!event.shiftKey) {
      event.preventDefault()
      this.saveNewItem(event)
    }
  }

  cancelNewItem(event) {
    const textarea = event.currentTarget
    const tempCard = textarea.closest(".roadmap-item")
    tempCard.remove()
  }

  updateColumnCount(columnId) {
    const column = this.element.querySelector(`.roadmap-column[data-column-id="${columnId}"]`)
    if (!column) return
    const container = column.querySelector('[data-roadmap-sortable-target="itemContainer"]')
    const countEl = column.querySelector(".item-count")
    if (container && countEl) {
      countEl.textContent = container.querySelectorAll(".roadmap-item").length
    }
  }

  // --- Drag & Drop ---

  async handleColumnSortEnd(event) {
    const columnElement = event.item
    const columnId = columnElement.dataset.columnId
    const newPosition = event.newIndex

    if (event.oldIndex === event.newIndex) return

    try {
      const response = await patch(`/workspace/roadmap_columns/${columnId}/reorder`, {
        body: JSON.stringify({ position: newPosition }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (!response.ok) {
        console.error("Failed to reorder column")
      }
    } catch (error) {
      console.error("Error reordering column:", error)
    }
  }

  async handleItemSortEnd(event) {
    const itemElement = event.item
    const itemId = itemElement.dataset.itemId
    const newColumnId = event.to.dataset.columnId
    const newPosition = event.newIndex

    if (event.from === event.to && event.oldIndex === event.newIndex) return

    try {
      const response = await patch(`/workspace/roadmap_items/${itemId}/reorder`, {
        body: JSON.stringify({
          roadmap_column_id: newColumnId,
          position: newPosition
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (!response.ok) {
        console.error("Failed to reorder item")
      }
    } catch (error) {
      console.error("Error reordering item:", error)
    }
  }
}
